import api from './api.ts';

export type WorkflowAccountType = 'whatsapp_business' | 'whatsapp_official' | 'instagram';
export interface WorkflowAccount {
  id: string;
  type: WorkflowAccountType;
  label: string;
  identifier: string | null;
  status: string;
  publishable: boolean;
  blocked_reason: string | null;
  capabilities: { any_message: boolean; ad_origin: boolean; can_execute: boolean; execution_blocked_reason: string | null };
}
export interface WorkflowDefinition {
  trigger: { type: 'message_received'; account_ids: string[]; origin: 'any' | 'ad' };
  action: { type: 'agents_sdk'; target: { type: 'clinic_agent' } };
}
export interface WorkflowMediaConfig {
  asset_id: string | null;
  media_type: 'image' | 'audio' | 'video';
  caption: string;
  context: { description: string; transcript: string; has_speech: boolean; reviewed: boolean };
}
export interface WorkflowBinding { source: 'form_field' | 'literal'; value: string }
export type WorkflowNode =
  | { id: string; type: 'form_trigger'; label: string; config: { webhook_id: number | null; account_id: string | null } }
  | { id: string; type: 'official_template'; label: string; config: { template_id: number | null; bindings: Record<string, WorkflowBinding>; header_media: WorkflowMediaConfig | null } }
  | { id: string; type: 'template_replies'; label: string; config: { template_node_id: string; timeout_hours: number; buttons: { index: number }[]; allow_free_text: true } }
  | { id: string; type: 'send_media'; label: string; config: WorkflowMediaConfig }
  | { id: string; type: 'clinic_agent'; label: string; config: Record<string, never> };
export interface WorkflowEdge { id: string; source: string; source_port: string; target: string | null }
export interface WorkflowGraphDefinition {
  schema_version: 2;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  presentation: { positions: Record<string, { x: number; y: number }>; viewport: { x: number; y: number; zoom: number } };
}
export type WorkflowDraft = WorkflowDefinition | WorkflowGraphDefinition;
export interface WorkflowAsset { id: string; display_name: string; media_type: WorkflowMediaConfig['media_type']; mime_type: string; byte_size: number; content_sha256: string }
export interface WorkflowTemplateResource {
  id: number; name: string; language: string; category: string; status: string; components: any[];
  requirements: Record<string, 'text' | 'url' | 'coupon_code' | 'image' | 'video' | 'document'>;
  blocked_reason: string | null;
}
export interface WorkflowResources {
  enabled: boolean;
  webhooks: { id: number; name: string; status: string; field_mapping: Record<string, unknown> }[];
  templates: WorkflowTemplateResource[];
  assets: WorkflowAsset[];
  agent: { configured: boolean; name?: string };
  execution: { enabled: boolean; pilot_allowed: boolean; schema_ready: boolean };
}
export interface WorkflowGraphSimulationInput { branch: string; human_mode: boolean; window_open: boolean; agent_hours_open: boolean }
export interface WorkflowGraphSimulationResult { synthetic: true; will_call_sdk: false; reason: string; errors?: string[]; steps: { node_id: string; label: string; status: string; reason?: string }[] }
export interface Workflow {
  id: number;
  name: string;
  description: string;
  status: 'draft' | 'published' | 'paused';
  priority: number;
  revision: number;
  published_version: number | null;
  published_priority?: number | null;
  draft: WorkflowDraft;
  published_definition: WorkflowDraft | null;
  created_at: string;
  updated_at: string;
  publication_scope?: 'configuration_only';
}
export interface WorkflowPayload {
  name: string;
  description?: string;
  priority: number;
  draft: WorkflowDraft;
  expected_revision?: number;
}
export interface WorkflowHistoryItem {
  kind: 'version' | 'decision' | 'lifecycle' | 'execution';
  id?: number;
  created_at?: string;
  version?: number;
  status?: string;
  reason?: string;
  account_id?: string;
  outcome?: string;
  steps?: { node_id: string; label: string; status: string; reason?: string; expires_at?: string; delivery_status?: string }[];
  [key: string]: unknown;
}
export interface WorkflowSimulationInput {
  account_id: string;
  origin: 'any' | 'ad' | 'unknown';
  has_ad_evidence: boolean;
  human_mode: boolean;
  sticky_flow_id?: number;
}
export interface WorkflowSimulationResult {
  synthetic: true;
  will_call_sdk: false;
  matched: boolean;
  reason: string;
  winner_flow_id: number | null;
  winner_version: number | null;
  target: { type: 'clinic_agent' } | null;
  can_execute: boolean;
  execution_blocked_reason: string | null;
}
export interface WorkflowValidationResult {
  valid: boolean;
  errors: string[];
  configuration_only: true;
  can_execute: boolean;
  execution_blocked_reasons: string[];
}

const base = '/api/workflows';
export const workflowsApi = {
  async resources(): Promise<WorkflowResources> {
    return (await api.get(`${base}/resources`)).data;
  },
  async uploadMedia(file: File, type: WorkflowMediaConfig['media_type']): Promise<WorkflowAsset> {
    const body = new FormData(); body.append('file', file); body.append('media_type', type);
    return (await api.post(`${base}/media`, body)).data;
  },
  async mediaPreview(id: string, signal: AbortSignal): Promise<Blob> {
    return (await api.get(`${base}/media/${encodeURIComponent(id)}/content`, { responseType: 'blob', signal })).data;
  },
  async simulateGraph(id: number, input: WorkflowGraphSimulationInput): Promise<WorkflowGraphSimulationResult> {
    return (await api.post(`${base}/${id}/simulate`, input)).data;
  },
  async list(): Promise<{ enabled: boolean; items: Workflow[] }> {
    return (await api.get(base)).data;
  },
  async accounts(): Promise<{ enabled: boolean; accounts: WorkflowAccount[] }> {
    return (await api.get(`${base}/accounts`)).data;
  },
  async create(payload: WorkflowPayload): Promise<Workflow> {
    return (await api.post(base, payload)).data;
  },
  async update(id: number, payload: WorkflowPayload): Promise<Workflow> {
    return (await api.put(`${base}/${id}`, payload)).data;
  },
  async transition(id: number, action: 'publish' | 'pause' | 'resume', revision: number): Promise<Workflow> {
    return (await api.post(`${base}/${id}/${action}`, { expected_revision: revision })).data;
  },
  async history(id: number): Promise<{ items: WorkflowHistoryItem[] }> {
    return (await api.get(`${base}/${id}/history`)).data;
  },
  async validate(id: number, revision: number): Promise<WorkflowValidationResult> {
    return (await api.post(`${base}/${id}/validate`, { expected_revision: revision })).data;
  },
  async simulate(id: number, input: WorkflowSimulationInput): Promise<WorkflowSimulationResult> {
    return (await api.post(`${base}/${id}/simulate`, input)).data;
  },
};
